import { useStore } from '../state/store';
import { Banner, Card, Chip, SectionHeader } from '../ui';
import { fmtCompact, fmtMoney, fmtPct } from '../utils/format';
import type { ProjectionYear } from '../engine/projection';

/**
 * Tax snapshot for a single projection year — typically the current year
 * (year 0) or the retirement year. Shows marginal rate, effective rate,
 * bracket-fill headroom (critical for Roth conversions), and IRMAA status.
 * Designed to surface the actionable tax insights at a glance.
 */
export function TaxCard({ year, label }: { year: ProjectionYear | undefined; label?: string }) {
  const { assumptions } = useStore();
  if (!year || !year.tax) return null;
  const t = year.tax;
  const irmaaActive = t.irmaaSurcharge > 0;
  const rmdActive = t.rmd > 0;

  // Visualize bracket fill: how much of the current bracket slice is used.
  // Shows at-a-glance room for Roth conversions in the current bracket.
  const bracketCeiling = t.headroomToNextBracket + t.taxableOrdinary;
  const fillPct =
    bracketCeiling > 0 ? Math.min(1, t.taxableOrdinary / bracketCeiling) : 0;

  return (
    <Card flush>
      <SectionHeader
        bordered
        muted
        title={`Tax snapshot ${label ? `· ${label}` : `· ${year.calendarYear}`}`}
        subtitle={`${assumptions.filingStatus === 'married' ? 'MFJ' : 'Single'} · age ${year.age}`}
        right={
          <div className="flex gap-1">
            <Chip size="sm" tone="accent">
              {fmtPct(t.marginalOrdinaryRate, 0)} marginal
            </Chip>
            <Chip size="sm" tone="neutral">
              {fmtPct(t.effectiveRate, 1)} eff.
            </Chip>
          </div>
        }
      />
      <div className="space-y-3 p-4">
        {/* Income summary — makes it explicit what's flowing in this year,
            especially useful in retirement when "wages = 0" but there's still
            $70k+ of LTCG being realized at the 0% federal rate. */}
        <div>
          <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-ink-500 dark:text-ink-300">
            Income
          </div>
          <div className="space-y-0.5">
            {t.income.wages > 0 && (
              <IncomeRow label="Wages / salary" value={t.income.wages} taxNote="ordinary + FICA" />
            )}
            {t.income.otherOrdinary > 0 && (
              <IncomeRow
                label="Other ordinary"
                value={t.income.otherOrdinary}
                taxNote="401(k) w/d, pension, Roth conv."
              />
            )}
            {t.income.ltcg > 0 && (
              <IncomeRow
                label="Realized LTCG"
                value={t.income.ltcg}
                taxNote={`taxed at ${fmtPct(t.marginalLTCGRate, 0)}${t.marginalLTCGRate === 0 ? ' — tax-free' : ''}`}
                highlight={t.marginalLTCGRate === 0}
              />
            )}
            {t.income.ssBenefits > 0 && (
              <IncomeRow
                label="Social Security"
                value={t.income.ssBenefits}
                taxNote={`${fmtMoney(t.ssTaxable)} taxable (${Math.round((t.ssTaxable / Math.max(1, t.income.ssBenefits)) * 100)}%)`}
              />
            )}
            {t.income.grossIncome === 0 && (
              <div className="text-xs text-ink-500 dark:text-ink-300">
                No income from wages, withdrawals, or benefits this year.
              </div>
            )}
            {t.income.grossIncome > 0 && (
              <div className="flex items-baseline justify-between border-t border-ink-100 pt-1 text-xs dark:border-ink-700">
                <span className="font-semibold text-ink-900 dark:text-ink-50">
                  Gross income
                </span>
                <span className="font-mono font-bold tabular-nums">
                  {fmtMoney(t.income.grossIncome)}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Bracket-fill bar — shows Roth-conversion headroom visually */}
        <div>
          <div className="flex items-baseline justify-between text-[11px] text-ink-500 dark:text-ink-300">
            <span>
              Ordinary bracket{' '}
              <span className="font-mono font-semibold text-ink-900 dark:text-ink-50">
                {fmtPct(t.marginalOrdinaryRate, 0)}
              </span>
            </span>
            <span className="font-mono tabular-nums">
              {fmtCompact(t.taxableOrdinary)} / {fmtCompact(bracketCeiling)}
            </span>
          </div>
          <div className="mt-1 h-2 overflow-hidden rounded-full bg-ink-100 dark:bg-ink-700">
            <div
              className="h-full rounded-full bg-accent-500"
              style={{ width: `${fillPct * 100}%` }}
            />
          </div>
          {t.headroomToNextBracket > 0 && isFinite(t.headroomToNextBracket) && (
            <div className="mt-1 text-[11px] text-ink-500 dark:text-ink-300">
              <span className="font-mono font-semibold text-pos-700 dark:text-pos-500">
                {fmtMoney(t.headroomToNextBracket)}
              </span>{' '}
              of headroom before next bracket jumps to
              <span className="font-mono font-semibold"> {fmtPct(marginalToNext(t), 0)}</span>
              {' '}— room for a Roth conversion at today's rate.
            </div>
          )}
        </div>

        {/* Breakdown grid */}
        <div className="grid grid-cols-2 gap-2 text-[11px]">
          <TaxRow label="Federal ordinary" value={t.federalOrdinaryTax} />
          <TaxRow label="Federal LTCG" value={t.federalLTCGTax} />
          <TaxRow label="FICA (SS+Med)" value={t.ficaTotal} dim={t.ficaTotal === 0} />
          <TaxRow label="Add'l Medicare 0.9%" value={t.additionalMedicareTax} dim={t.additionalMedicareTax === 0} />
          <TaxRow label="NIIT 3.8%" value={t.niitTax} dim={t.niitTax === 0} />
          <TaxRow label="State" value={t.stateTax} dim={t.stateTax === 0} />
          <TaxRow
            label="Total tax"
            value={t.totalTax}
            emphasize
          />
          <TaxRow
            label="LTCG bracket"
            value={0}
            renderValue={<span>{fmtPct(t.marginalLTCGRate, 0)}</span>}
          />
        </div>

        {irmaaActive && (
          <Banner tone="warning" title="IRMAA surcharge applies">
            MAGI of {fmtMoney(t.magi)} triggers a Medicare surcharge of{' '}
            <span className="font-mono font-semibold">{fmtMoney(t.irmaaSurcharge)}/yr</span>{' '}
            per person. Consider bracket management to stay under the next tier.
          </Banner>
        )}

        {rmdActive && (
          <Banner tone="info" title="Required Minimum Distribution">
            At age {year.age}, you must withdraw at least{' '}
            <span className="font-mono font-semibold">{fmtMoney(t.rmd)}</span>{' '}
            from the 401(k)/IRA this year. The engine assumes this flows to taxable savings.
          </Banner>
        )}

        {t.ssTaxable > 0 && (
          <div className="text-[11px] text-ink-500 dark:text-ink-300">
            Of your SS benefits,{' '}
            <span className="font-mono font-semibold">{fmtMoney(t.ssTaxable)}</span> is
            taxable this year (50%/85% rule).
          </div>
        )}
      </div>
    </Card>
  );
}

function IncomeRow({
  label,
  value,
  taxNote,
  highlight,
}: {
  label: string;
  value: number;
  taxNote: string;
  highlight?: boolean;
}) {
  return (
    <div className={`flex items-baseline justify-between gap-2 rounded-md px-2 py-1 text-xs ${
      highlight ? 'bg-pos-50 dark:bg-pos-700/10' : ''
    }`}>
      <span className="min-w-0 flex-1 truncate">
        <span className="font-semibold text-ink-900 dark:text-ink-50">{label}</span>{' '}
        <span className="text-[10px] text-ink-500 dark:text-ink-300">{taxNote}</span>
      </span>
      <span className="shrink-0 font-mono tabular-nums">
        {fmtMoney(value)}
      </span>
    </div>
  );
}

function TaxRow({
  label,
  value,
  dim,
  emphasize,
  renderValue,
}: {
  label: string;
  value: number;
  dim?: boolean;
  emphasize?: boolean;
  renderValue?: React.ReactNode;
}) {
  return (
    <div
      className={`flex items-baseline justify-between rounded-md px-2 py-1 ${
        emphasize
          ? 'bg-ink-50 font-semibold dark:bg-ink-800/60'
          : dim
            ? 'opacity-50'
            : ''
      }`}
    >
      <span className="text-ink-500 dark:text-ink-300">{label}</span>
      <span className="font-mono tabular-nums">
        {renderValue ?? (value === 0 ? '—' : fmtMoney(value))}
      </span>
    </div>
  );
}

/** Next-bracket rate at the current position (crude — uses the same data
 *  as headroomToNextBracket). We don't have it on the result object, so
 *  infer from the marginal + a tiny probe. */
function marginalToNext(t: ProjectionYear['tax']): number {
  // The engine's AnnualTaxResult already has marginalOrdinaryRate; the NEXT
  // bracket's rate isn't carried. Approximate: if we're in 10%, next is 12;
  // 12→22; 22→24; 24→32; 32→35; 35→37; 37→37. This matches the 2026 schedule.
  const r = t.marginalOrdinaryRate;
  const next: Record<number, number> = {
    0.10: 0.12, 0.12: 0.22, 0.22: 0.24, 0.24: 0.32, 0.32: 0.35, 0.35: 0.37, 0.37: 0.37,
  };
  return next[r] ?? r;
}
